<template>
    <div class="lds-container clnt-output-card-page">
        <div class="clnt-output-card">
            <div class="clnt-output-card__shell">
                <header class="clnt-output-card__header">
                    <div class="clnt-output-card__title-wrap">
                        <div class="clnt-output-card__title-icon"
                             aria-hidden="true">
                            <span class="clnt-output-card__title-icon-chip" />
                        </div>
                        <h1 class="clnt-output-card__title">
                            Банковская карта
                        </h1>
                    </div>
                    <div class="clnt-output-card__actions">
                        <button class="clnt-output-card__action"
                                type="button"
                                aria-label="Назад к выводу средств"
                                data-testid="create-debit-card-back"
                                @click="navigateToOutput">
                            <SvgIcon name="arrow-left" />
                        </button>
                        <button class="clnt-output-card__action"
                                type="button"
                                aria-label="Закрыть форму добавления карты"
                                data-testid="create-debit-card-close"
                                @click="navigateToOutput">
                            <SvgIcon name="close" />
                        </button>
                    </div>
                </header>
                <form @submit.prevent="createCard()"
                      class="clnt-output-card__form">
                    <div class="clnt-output-card__grid">
                        <div class="clnt-output-card__field clnt-output-card__field_grid">
                            <div class="lds-control"
                                 :class="{'lds-control_error': $v.cardNumber.$error}">
                                <label for="card-number"
                                       class="lds-control__label lds-control__label_lg">Номер карты</label>
                                <input id="card-number"
                                       type="text"
                                       name="cardNumber"
                                       class="lds-input lds-input_size-md"
                                       v-mask="configCardNumber.mask"
                                       placeholder="0000 0000 0000 0000"
                                       v-model="cardNumber"
                                       @change="$v.cardNumber.$touch()"
                                       autocomplete="off">
                                <div class="lds-control__message"
                                     v-if="$v.cardNumber.$error">
                                    Проверьте введенные данные
                                </div>
                            </div>
                        </div>
                        <div class="clnt-output-card__field clnt-output-card__field_grid">
                            <div class="lds-control"
                                 :class="{'lds-control_error': $v.cardExpired.$error}">
                                <label for="card-expired"
                                       class="lds-control__label lds-control__label_lg">Срок действия карты</label>
                                <input id="card-expired"
                                       type="text"
                                       name="cardExpired"
                                       class="lds-input lds-input_size-md"
                                       v-mask="configCardExpired.mask"
                                       placeholder="ММ/ГГ"
                                       v-model.trim="cardExpired"
                                       @change="$v.cardExpired.$touch()"
                                       autocomplete="off">
                                <div class="lds-control__message"
                                     v-if="$v.cardExpired.$error">
                                    Проверьте введенные данные
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="clnt-output-card__field">
                        <div class="lds-control"
                             :class="{'lds-control_error': $v.cardCountryCode.$error}">
                            <label class="lds-control__label lds-control__label_lg">Страна выпуска карты</label>
                            <VSelect class="lds-select lds-select_size-md"
                                     id="cardCountryCode"
                                     placeholder="Выберите страну"
                                     v-model="cardCountryCode"
                                     @open="selectClick()"
                                     :options="countries"
                                     :append-to-body="true"
                                     :calculate-position="calculateCountryDropdownPosition"
                                     label="title"
                                     @search="countySelectInput" />
                            <div class="lds-control__message"
                                 v-if="$v.cardCountryCode.$error">
                                Обязательно для заполнения
                            </div>
                        </div>
                    </div>
                    <div class="clnt-output-card__field">
                        <div class="lds-control"
                             :class="{'lds-control_error': $v.cardHolder.$error}">
                            <label for="card-holder"
                                   class="lds-control__label lds-control__label_lg">Держатель карты</label>
                            <input id="card-holder"
                                   type="text"
                                   name="cardHolder"
                                   class="lds-input lds-input_size-md"
                                   placeholder="IVANOV IVAN"
                                   v-model="cardHolder"
                                   @change="$v.cardHolder.$touch()"
                                   autocomplete="off">
                            <div class="lds-control__message"
                                 v-if="$v.cardHolder.$error && !$v.cardHolder.required">
                                Обязательно для заполнения
                            </div>
                            <div class="lds-control__message"
                                 v-if="$v.cardHolder.$error && $v.cardHolder.required && !$v.cardHolder.isValidHolder">
                                Имя и Фамилия должны содержать латинские буквы
                            </div>
                        </div>
                    </div>
                    <div class="clnt-output-card__footer">
                        <button class="lds-btn lds-btn_fill lds-btn_size-xs -green"
                                type="submit"
                                :disabled="disableSubmit">
                            Добавить карту
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import {required} from 'vuelidate/lib/validators';
import {mask} from 'vue-the-mask';
import vSelect from 'vue-select';
import SvgIcon from '@/components/common/SvgIcon';
import {watchSelectDropdownPositionByComponent} from '@/utils/selectDropdownPosition';

export default {
    name: 'CreateDebitCard',
    components: {
        SvgIcon,
        vSelect,
    },
    directives: {
        mask,
    },
    beforeRouteEnter(
        to, from, next,
    ) {
        next(vm => {
            vm.canReturnToOutputViaHistory = from.name === 'OutputLC';
        });
    },
    beforeRouteUpdate(
        to, from, next,
    ) {
        this.canReturnToOutputViaHistory = from.name === 'OutputLC';
        next();
    },
    data: () => ({
        configCardNumber: {
            type: 'text',
            mask: '#### #### #### #### ##',
        },
        configCardExpired: {
            type: 'text',
            mask: '##/##',
        },
        cardNumber: '',
        cardExpired: '',
        cardCountryCode: null,
        cardHolder: '',
        canReturnToOutputViaHistory: false,
    }),
    computed: {
        ...mapState('outputLC', [
            'countries',
            'processing',
        ]),
        disableSubmit() {
            return this.processing;
        },
    },
    watch: {
        cardHolder(value) {
            if (!value) {
                return '';
            }
            this.cardHolder = value.toString().toUpperCase();
        },
    },
    created() {
        this.getCountries();
    },
    validations: {
        cardNumber: {
            required,
            isValidNumber(cardNumber) {
                const regex = /(\d{4}[ ]?){3}\d(\d{3}[ ]?)?(\d{3})?/;
                return regex.test(cardNumber);
            },
        },
        cardExpired: {
            required,
            isValidMonthAndYear(value) {
                const regex = /^(\d{2}).(\d{2})/;
                const matched = regex.test(value);

                if (!matched) {
                    return false;
                }

                const dateAsArray = value.split('/');
                const currentDate = new Date();
                const inputMonth = parseInt(dateAsArray[0]);
                const inputYear = 2000 + parseInt(dateAsArray[1]);
                const currentYear = currentDate.getFullYear();
                const currentMonth = currentDate.getMonth() + 1;

                if (inputMonth < 1 || inputMonth > 12) {
                    return false;
                }

                if (inputYear > currentYear) {
                    return true;
                } if (inputYear < currentYear) {
                    return false;
                } if (inputYear === currentYear) {
                    return inputMonth >= currentMonth;
                }
                return true;
            },
        },
        cardCountryCode: {
            required,
        },
        cardHolder: {
            required,
            isValidHolder(cardHolder) {
                const regex = /^([A-Za-z]+[-]?)\w+ ([A-Za-z]+[-]?)\w+$/;
                return regex.test(cardHolder);
            },
        },
    },
    methods: {
        ...mapActions('outputLC', [
            'getCountries',
            'createDebitCard',
        ]),
        async createCard() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            const data = {
                cardNumber: this.cardNumber.replace(/\s+/g, ''),
                cardCountryCode: this.cardCountryCode.id,
                cardHolderName: this.cardHolder,
                cardExpireMonth: this.cardExpired.split('/')[0],
                cardExpireYear: this.cardExpired.split('/')[1],
            };

            const result = await this.createDebitCard(data);
            if (result) {
                this.$router.push({
                    name: 'OutputLC',
                });
            }
        },
        countySelectInput(value) {
            this.getCountries(value);
        },
        navigateToOutput() {
            if (this.canReturnToOutputViaHistory) {
                this.$router.back();
                return;
            }

            this.$router.replace({
                name: 'OutputLC',
            });
        },
        calculateCountryDropdownPosition(dropdownList, component) {
            return watchSelectDropdownPositionByComponent(dropdownList, component);
        },
        selectClick() {
            this.cardCountryCode = null;
        },
    },
};
</script>

<style scoped>
.clnt-output-card-page {
    padding: 32px 16px 48px;
}

.clnt-output-card {
    max-width: 760px;
    margin: 0 auto;
}

.clnt-output-card__shell {
    background: #ffffff;
    border: 1px solid #e7ebf3;
    border-radius: 24px;
    box-shadow: 0 18px 44px rgba(29, 36, 56, 0.1);
}

.clnt-output-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 20px 16px;
    border-bottom: 1px solid #edf1f7;
}

.clnt-output-card__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.clnt-output-card__title-icon {
    position: relative;
    width: 34px;
    height: 24px;
    flex: 0 0 auto;
    border-radius: 8px;
    background: linear-gradient(135deg, #f7c64f 0%, #f0a51a 100%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.clnt-output-card__title-icon::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 7px;
    width: 20px;
    height: 4px;
    border-radius: 999px;
    background: rgba(86, 52, 8, 0.28);
}

.clnt-output-card__title-icon-chip {
    position: absolute;
    right: 7px;
    bottom: 5px;
    width: 7px;
    height: 7px;
    border-radius: 2px;
    background: rgba(86, 52, 8, 0.3);
}

.clnt-output-card__title {
    margin: 0;
    color: #1f2a44;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
}

.clnt-output-card__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.clnt-output-card__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #e1e7f0;
    border-radius: 50%;
    background: #f8fafc;
    color: #5b6475;
}

.clnt-output-card__action:hover {
    background: #eef4ff;
    border-color: #bfd1f3;
    color: #1f2a44;
    transform: translateY(-1px);
}

.clnt-output-card__action ::v-deep .lds-icon {
    width: 16px;
    height: 16px;
}

.clnt-output-card__form {
    padding: 20px;
}

.clnt-output-card__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
    align-items: start;
}

.clnt-output-card__field + .clnt-output-card__field,
.clnt-output-card__grid + .clnt-output-card__field {
    margin-top: 18px;
}

.clnt-output-card__grid > .clnt-output-card__field {
    margin-top: 0;
}

.clnt-output-card__field_grid {
    display: flex;
}

.clnt-output-card__field_grid .lds-control {
    width: 100%;
}

.clnt-output-card__field ::v-deep .lds-input,
.clnt-output-card__field ::v-deep .lds-select,
.clnt-output-card__field ::v-deep .vs__dropdown-toggle {
    min-height: 56px;
    border-radius: 16px;
}

.clnt-output-card__field ::v-deep .vs__dropdown-toggle {
    padding: 8px 14px;
}

.clnt-output-card__footer {
    display: flex;
    justify-content: center;
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid #edf1f7;
}

.clnt-output-card__footer .lds-btn {
    min-width: 220px;
}

@media (max-width: 767px) {
    .clnt-output-card-page {
        padding: 16px 10px 32px;
    }

    .clnt-output-card__shell {
        border-radius: 22px;
    }

    .clnt-output-card__header {
        align-items: center;
        padding: 16px 18px 14px;
    }

    .clnt-output-card__form {
        padding: 18px;
    }

    .clnt-output-card__grid {
        grid-template-columns: 1fr;
        gap: 0;
    }

    .clnt-output-card__field + .clnt-output-card__field,
    .clnt-output-card__grid + .clnt-output-card__field {
        margin-top: 16px;
    }

    .clnt-output-card__grid > .clnt-output-card__field {
        margin-top: 0;
    }

    .clnt-output-card__footer {
        justify-content: stretch;
    }

    .clnt-output-card__footer .lds-btn {
        width: 100%;
        min-width: 0;
    }
}
</style>
